import { observer } from 'mobx-react-lite';
import { desktopUpdateStore } from '@/desktop/desktop-update-state';
import { getDesktop } from '@/desktop/desktop';
import { CloseIcon, DownloadIcon } from '@components/icons';

/**
 * Persistent desktop-update toast: stays up through the download with live
 * progress, then becomes the restart prompt. Errors go through the regular
 * toast (use-desktop-updates); this renders nothing for them.
 */
export const DesktopUpdateToast = observer(function DesktopUpdateToast() {
  const { state, dismissed } = desktopUpdateStore;
  if (!state || dismissed) return null;
  if (state.phase !== 'downloading' && state.phase !== 'ready' && state.phase !== 'installing') {
    return null;
  }

  const version = state.version ?? '';

  return (
    <div className="update-toast-container" data-testid="update-toast">
      <div className="toast-item entering border-cyan-200 bg-cyan-50 text-cyan-800 dark:border-cyan-700/50 dark:bg-cyan-900/80 dark:text-cyan-200">
        <DownloadIcon className="h-5 w-5 flex-shrink-0 text-cyan-500" />
        <div className="flex-1">
          {state.phase === 'downloading' && (
            <>
              <div>
                Downloading SKJV Bible Study {version}
                {state.progress !== null ? ` — ${state.progress}%` : '…'}
              </div>
              <div className="update-toast-track">
                <div
                  className="update-toast-bar"
                  style={{ width: `${Math.max(state.progress ?? 0, 3)}%` }}
                />
              </div>
            </>
          )}
          {state.phase === 'ready' && (
            <>
              <div>Update {version} is ready.</div>
              <div className="mt-2 flex gap-2">
                <button
                  className="update-toast-button"
                  data-testid="update-restart"
                  onClick={() => void getDesktop()?.updates.restart()}
                >
                  Restart now
                </button>
                <button
                  className="update-toast-button-quiet"
                  data-testid="update-later"
                  onClick={() => desktopUpdateStore.dismiss()}
                >
                  Later
                </button>
              </div>
              <div className="mt-1 text-xs opacity-70">
                Choosing Later finishes the update next time you quit.
              </div>
            </>
          )}
          {state.phase === 'installing' && <div>Restarting to finish the update…</div>}
        </div>
        {state.phase === 'downloading' && (
          <button
            onClick={() => desktopUpdateStore.dismiss()}
            className="rounded-full p-1 transition-colors hover:bg-black/10 dark:hover:bg-white/10"
            title="Hide (the download continues)"
          >
            <CloseIcon className="h-4 w-4 opacity-60" />
          </button>
        )}
      </div>
    </div>
  );
});

export default DesktopUpdateToast;
